import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { ShieldCheck, ShieldOff, Copy, Download, CheckCircle } from 'lucide-react';

const SecuritySettings = () => {
  const { user } = useAuth();
  const [step, setStep] = useState('overview'); // overview | setup | verify | complete
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);

  const is2FAEnabled = user?.totp_enabled;

  const handleSetup = async () => {
    setLoading(true);
    try {
      const res = await api.setup2FA();
      setQrCode(res.data.qr_code);
      setSecret(res.data.secret);
      setBackupCodes(res.data.backup_codes);
      setStep('setup');
    } catch (err) {
      toast.error('Kunde inte starta 2FA-setup');
    } finally { setLoading(false); }
  };

  const handleActivate = async () => {
    if (verifyCode.length !== 6) { toast.error('Ange en 6-siffrig kod'); return; }
    setLoading(true);
    try {
      await api.activate2FA(verifyCode);
      toast.success('2FA aktiverad!');
      setStep('complete');
      // Refresh page to update user state
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ogiltig kod. Försök igen.');
    } finally { setLoading(false); }
  };

  const handleDisable = async () => {
    if (!window.confirm('Är du säker att du vill inaktivera tvåstegsverifiering?')) return;
    setLoading(true);
    try {
      await api.disable2FA();
      toast.success('2FA inaktiverad');
      window.location.reload();
    } catch (err) {
      toast.error('Kunde inte inaktivera 2FA');
    } finally { setLoading(false); }
  };

  const downloadBackupCodes = () => {
    const text = `Operio Reservkoder\n${'='.repeat(30)}\n\nAnvänd dessa koder om du förlorar åtkomst till din autentiseringsapp.\nVarje kod kan bara användas en gång.\n\n${backupCodes.join('\n')}\n`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'operio-reservkoder.txt';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-8" data-testid="security-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Säkerhet</h1>
        <p className="text-muted-foreground text-sm mt-1">Hantera tvåstegsverifiering (2FA)</p>
      </div>

      {/* Overview */}
      {step === 'overview' && (
        <div className="bg-card border border-border rounded-xl p-6 max-w-lg">
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${is2FAEnabled ? 'bg-green-50 text-green-600' : 'bg-muted text-muted-foreground'}`}>
              {is2FAEnabled ? <ShieldCheck size={24} /> : <ShieldOff size={24} />}
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-base">Tvåstegsverifiering (2FA)</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {is2FAEnabled
                  ? 'Aktiverad. Ditt konto skyddas med en autentiseringsapp.'
                  : 'Inaktiverad. Aktivera för extra säkerhet med Google Authenticator eller liknande.'}
              </p>
              <div className="mt-4">
                {is2FAEnabled ? (
                  <Button variant="destructive" size="sm" onClick={handleDisable} disabled={loading} data-testid="disable-2fa-btn">
                    <ShieldOff size={14} className="mr-2" />
                    {loading ? 'Inaktiverar...' : 'Inaktivera 2FA'}
                  </Button>
                ) : (
                  <Button onClick={handleSetup} disabled={loading} data-testid="enable-2fa-btn"
                    className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white">
                    <ShieldCheck size={14} className="mr-2" />
                    {loading ? 'Laddar...' : 'Aktivera 2FA'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Setup - QR Code */}
      {step === 'setup' && (
        <div className="bg-card border border-border rounded-xl p-6 max-w-lg space-y-6">
          <div>
            <h3 className="font-semibold text-base mb-1">Steg 1: Skanna QR-koden</h3>
            <p className="text-sm text-muted-foreground">
              Öppna Google Authenticator (eller annan TOTP-app) och skanna koden nedan.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="p-4 bg-card rounded-xl border border-border shadow-sm">
              <img src={qrCode} alt="QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-xs text-muted-foreground mb-1">Eller ange manuellt:</p>
            <div className="flex items-center gap-2">
              <code className="text-sm font-mono flex-1 break-all" data-testid="2fa-secret-key">{secret}</code>
              <button onClick={() => { navigator.clipboard.writeText(secret); toast.success('Kopierat!'); }}
                className="p-1.5 hover:bg-muted rounded transition-colors" data-testid="copy-secret-btn">
                <Copy size={14} />
              </button>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-base mb-1">Steg 2: Verifiera</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Ange den 6-siffriga koden från din autentiseringsapp.
            </p>
            <div className="flex gap-2">
              <Input
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                data-testid="2fa-verify-input"
                className="h-11 text-center text-lg tracking-widest max-w-[160px]"
              />
              <Button onClick={handleActivate} disabled={loading || verifyCode.length !== 6} data-testid="activate-2fa-btn"
                className="h-11 bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white">
                {loading ? 'Verifierar...' : 'Aktivera'}
              </Button>
            </div>
          </div>

          <div className="border-t border-border pt-4">
            <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
              Reservkoder
              <span className="text-xs font-normal bg-amber-50 text-amber-700 px-2 py-0.5 rounded">Spara dessa!</span>
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Dessa koder kan användas om du förlorar din telefon. Varje kod fungerar bara en gång.
            </p>
            <div className="grid grid-cols-2 gap-2 mb-3" data-testid="backup-codes-grid">
              {backupCodes.map((code, i) => (
                <code key={i} className="text-sm font-mono bg-muted/50 px-3 py-1.5 rounded text-center">{code}</code>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={downloadBackupCodes} data-testid="download-backup-codes-btn">
              <Download size={14} className="mr-2" /> Ladda ner reservkoder
            </Button>
          </div>

          <Button variant="ghost" size="sm" onClick={() => setStep('overview')}>Avbryt</Button>
        </div>
      )}

      {/* Complete */}
      {step === 'complete' && (
        <div className="bg-card border border-border rounded-xl p-6 max-w-lg text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-green-50 mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h3 className="font-semibold text-lg mb-2">2FA aktiverad!</h3>
          <p className="text-sm text-muted-foreground">
            Ditt konto skyddas nu med tvåstegsverifiering. Vid nästa inloggning behöver du ange en kod från din autentiseringsapp.
          </p>
        </div>
      )}
    </div>
  );
};

export default SecuritySettings;
